/**
 * Small reusable "Show test data" toggle for admin list pages.
 *
 * By default, admin endpoints hide synthetic test/demo records. This toggle
 * lets the admin reveal them when needed (e.g. debugging a flaky test).
 *
 * Persists state in localStorage per `storageKey` so admins don't have to
 * re-toggle on every page reload.
 */
import { useEffect, useState } from "react";
import { Beaker } from "lucide-react";

export function useShowDemoToggle(storageKey = "admin.showDemo") {
  const [showDemo, setShowDemo] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, showDemo ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [showDemo, storageKey]);
  return [showDemo, setShowDemo];
}

export function ShowTestDataToggle({ showDemo, onChange, testId = "admin-show-demo-toggle" }) {
  return (
    <label
      className="inline-flex items-center gap-2 cursor-pointer select-none text-xs text-slate-600 hover:text-slate-900 transition-colors"
      data-testid={`${testId}-label`}
    >
      <input
        type="checkbox"
        checked={!!showDemo}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
      />
      <Beaker className="w-3.5 h-3.5 text-slate-400" />
      <span>Show test data</span>
    </label>
  );
}

export default ShowTestDataToggle;
